<template>
  <div>
    <h2 class="body-1-bold mb-16">
      {{ i18n(translations.title) }}
    </h2>

    <div class="flex flex-wrap gap-12">
      <div v-if="!isEmpty(cards)" class="flex flex-col gap-8">
        <h3 class="body-2">{{ i18n(translations.cards) }}</h3>
        <div class="flex flex-nowrap items-center gap-4">
          <RevIllustration
            v-for="({ icon, label }, id) in cards"
            :key="id"
            :alt="label"
            :height="27"
            :src="icon"
            :width="48"
          />
        </div>
      </div>

      <div v-if="!isEmpty(konbini)" class="flex flex-col gap-8">
        <h3 class="body-2">{{ i18n(translations.konbini) }}</h3>
        <div class="flex flex-nowrap items-center gap-4">
          <RevIllustration
            v-for="({ icon, label }, id) in konbini"
            :key="id"
            :alt="label"
            :height="27"
            :src="icon"
            :width="48"
          />
        </div>
      </div>

      <div v-if="!isEmpty(qrcode)" class="flex flex-col gap-8">
        <h3 class="body-2">{{ i18n(translations.qrcode) }}</h3>
        <div class="flex flex-nowrap items-center gap-4">
          <RevIllustration
            v-for="({ icon, label }, id) in qrcode"
            :key="id"
            :alt="label"
            :height="27"
            :src="icon"
            :width="48"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type {
  PaymentMethod,
  PaymentNetwork,
} from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import {
  isPaymentMethodEnabled,
  paymentNetworkConfig,
  paymentNetworkIcon,
} from '@backmarket/nuxt-layer-payment/methods'
import { usePaymentMethodsWithFallback } from '@backmarket/nuxt-layer-payment/methods/composables/usePaymentMethodsWithFallback'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevIllustration } from '@ds/components/Illustration'

import {
  PAYMENT_NETWOKS_KONBINI,
  PAYMENT_NETWORKS_QR_CODE,
} from './PaymentMethods.constants'
import translations from './PaymentMethods.translations'

const i18n = useI18n()

export type PaymentIconType = 'networks'

const props = withDefaults(
  defineProps<{
    methods?: PaymentMethod[] | PaymentMethod['bmCode'][] | null
  }>(),
  {
    methods: undefined,
  },
)

const paymentMethods = usePaymentMethodsWithFallback(() => props.methods)

const displayedMethods = computed(() => {
  return paymentMethods.value.filter((method) => isPaymentMethodEnabled(method))
})

const getNetworkIcons = (networks: PaymentNetwork[] = []) => {
  return networks.reduce((acc, networkId) => {
    const icon = paymentNetworkIcon(networkId)
    const { name } = paymentNetworkConfig(networkId)

    return isEmpty(icon)
      ? acc
      : {
          ...acc,
          [networkId]: {
            icon,
            label: typeof name === 'string' ? name : i18n(name),
          },
        }
  }, {})
}

const networkIcons = computed<Record<string, object>>(() =>
  displayedMethods.value.reduce(
    (acc, { networks }) => ({
      ...acc,
      ...getNetworkIcons(networks),
    }),
    {},
  ),
)

const cards = computed(() => {
  return Object.keys(networkIcons.value).reduce(
    (acc, cur) => ({
      ...acc,
      ...insertIf(
        !PAYMENT_NETWOKS_KONBINI.includes(cur) &&
          !PAYMENT_NETWORKS_QR_CODE.includes(cur),
        { [cur]: networkIcons.value[cur] },
      ),
    }),
    {},
  )
})

const konbini = computed(() => {
  return Object.keys(networkIcons.value).reduce(
    (acc, cur) => ({
      ...acc,
      ...insertIf(PAYMENT_NETWOKS_KONBINI.includes(cur), {
        [cur]: networkIcons.value[cur],
      }),
    }),
    {},
  )
})

const qrcode = computed(() => {
  return Object.keys(networkIcons.value).reduce(
    (acc, cur) => ({
      ...acc,
      ...insertIf(PAYMENT_NETWORKS_QR_CODE.includes(cur), {
        [cur]: networkIcons.value[cur],
      }),
    }),
    {},
  )
})
</script>
